import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import ReCAPTCHA from "react-google-recaptcha";
import PageTitle from "../../../layouts/PageTitle";
import QuantityStepper from "./QuantityStepper";
import { useTransferSearch } from "../../../../context/TransferSearchContext";
import { computeEffectivePrice, createBooking, formatApiError, getExtrasPricing } from "../../../../services/TransferService";
import EXTRAS_META from "./extrasMeta";

const NO_OF_CHILD_OPTIONS = Array.from({ length: 9 }, (_, i) => i); // 0..8
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_INVISIBLE_SITE_KEY;

function formatSearchDate(date) {
	return date ? moment(date).format("DD MMM YYYY") : "—";
}

function formatSearchTime(time) {
	if (!Number.isInteger(time?.hour) || !Number.isInteger(time?.minute)) return "—";
	return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`;
}

const TransferBookingDetails = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const agent = useSelector((state) => state.auth.auth) || {};
	const { searchData } = useTransferSearch();
	const transferData = location.state?.transferData;

	// No longer user-editable here — private transfers price per vehicle, not per
	// passenger, so this just records whatever passenger count was searched with.
	const passengerQty = searchData.passengers > 0 ? searchData.passengers : 1;
	const [extrasPricing, setExtrasPricing] = useState(null);
	const [extrasQty, setExtrasQty] = useState({ luggage: 0, surfboard: 0, babySeats: 0, babyBooster: 0 });
	const [form, setForm] = useState({
		passengerName: "",
		phone: "",
		flightDetails: "",
		email: agent.email || "",
		specialRequirements: "",
		noOfChild: 0,
		agencyRefNo: "",
		agencyStaff: "",
	});
	const [submitting, setSubmitting] = useState(false);
	const recaptchaRef = useRef(null);

	useEffect(() => {
		getExtrasPricing()
			.then(setExtrasPricing)
			.catch(() => setExtrasPricing(null));
	}, []);

	if (!transferData) {
		return (
			<div className="card">
				<div className="card-body text-center py-5">
					<p className="mb-3">No vehicle selected. Please start a new search.</p>
					<button type="button" className="btn btn-primary" onClick={() => navigate("/dashboard")}>
						Back to Search
					</button>
				</div>
			</div>
		);
	}

	const { effective: unitPrice } = computeEffectivePrice(transferData);

	function extrasTotal(key) {
		if (!extrasPricing) return 0;
		const meta = EXTRAS_META.find((m) => m.key === key);
		return extrasQty[key] * (Number(extrasPricing[meta.priceKey]) || 0);
	}

	const extrasGrandTotal = EXTRAS_META.reduce((sum, m) => sum + extrasTotal(m.key), 0);
	// Private transfers are priced per vehicle, not per passenger — the vehicle price
	// already covers everyone up to its max_pax capacity.
	const totalPrice = unitPrice + extrasGrandTotal;

	function handleFormChange(e) {
		setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	}

	const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
	const isFormValid =
		form.passengerName.trim() !== "" &&
		form.phone.trim() !== "" &&
		isValidEmail(form.email) &&
		form.agencyStaff.trim() !== "";

	async function handleProceedClick() {
		if (!isFormValid || submitting) return;
		setSubmitting(true);
		// RECAPTCHA_SITE_KEY may be unset in environments where it hasn't been configured yet
		// (see .env.example) - don't block bookings on a missing key, just skip verification.
		if (!RECAPTCHA_SITE_KEY) {
			await handleSubmit(null);
			return;
		}
		try {
			const token = await recaptchaRef.current.executeAsync();
			recaptchaRef.current.reset();
			if (!token) {
				formatApiError(null, "Verification failed. Please try again.");
				setSubmitting(false);
				return;
			}
			await handleSubmit(token);
		} catch (e) {
			formatApiError(null, "Verification failed. Please try again.");
			setSubmitting(false);
		}
	}

	async function handleSubmit(recaptchaToken) {
		const payload = {
			vehicle_id: transferData.vehicle_id,
			user_name: form.passengerName,
			no_of_pax: passengerQty,
			no_of_child: form.noOfChild || 0,
			user_email: form.email,
			user_phone: form.phone,
			booking_date: moment(searchData.date).format("DD/MM/YYYY"),
			booking_time: formatSearchTime(searchData.time),
			flight_number: form.flightDetails || "-",
			requirements: form.specialRequirements || "-",
			from_address: searchData.from?.label || "",
			to_address: searchData.to?.label || "",
			price_single: unitPrice,
			price_total: totalPrice,
			pay_type: "book_now",
			extra_luggage: extrasQty.luggage,
			surfboard: extrasQty.surfboard,
			baby_seats: extrasQty.babySeats,
			baby_booster: extrasQty.babyBooster,
			extra_luggage_price: extrasTotal("luggage"),
			surfboard_price: extrasTotal("surfboard"),
			baby_seats_price: extrasTotal("babySeats"),
			baby_booster_price: extrasTotal("babyBooster"),
			currency: agent.currency || "AUD",
			agent_id: agent.id,
			agency_ref_no: form.agencyRefNo || "",
			agency_staff: form.agencyStaff,
			recaptcha_token: recaptchaToken,
		};

		try {
			const data = await createBooking(payload);
			navigate("/transfer-payment", {
				state: {
					bookingId: data.booking_id,
					// Use the amount we actually computed and sent as price_total — it reflects
					// the current passenger count and extras from this screen. The API's own
					// charge_amount echo isn't reliable here, so don't let it override this.
					totalCharge: totalPrice,
					bookingMessage: data.success || data.message || "",
					transferData,
					searchData,
				},
			});
		} catch (error) {
			if (error.response) {
				formatApiError(error.response.data, "Unable to create booking. Please try again.");
			} else {
				formatApiError(null, "Unable to reach the server. Please try again.");
			}
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="transfer-flow">
			<PageTitle activeMenu="Booking Details" motherMenu="Dashboard" />
			<div className="row align-items-start">
				<div className="col-lg-8">
					<div className="card">
						<div className="card-body">
							<h4 className="mb-3">Let us know who you are</h4>
							<div className="row g-3">
								<div className="col-md-6">
									<label className="form-label">Passenger Name <span className="text-danger">*</span></label>
									<input type="text" name="passengerName" className="form-control" value={form.passengerName} onChange={handleFormChange} placeholder="Name of passenger" required />
								</div>
								<div className="col-md-6">
									<label className="form-label">Phone <span className="text-danger">*</span></label>
									<input type="tel" name="phone" className="form-control" value={form.phone} onChange={handleFormChange} placeholder="+61 4XX XXX XXX" required />
								</div>
								<div className="col-md-6">
									<label className="form-label">Flight / Cruise / Train Name &amp; Number</label>
									<input type="text" name="flightDetails" className="form-control" value={form.flightDetails} onChange={handleFormChange} placeholder="e.g. Flight QF1, Cruise ship name, or Train name & number" />
								</div>
								<div className="col-md-6">
									<label className="form-label">Email Address <span className="text-danger">*</span></label>
									<input type="email" name="email" className="form-control" value={form.email} onChange={handleFormChange} placeholder="Email of passenger" required />
								</div>
								<div className="col-md-6">
									<label className="form-label">No. of Children</label>
									<select name="noOfChild" className="form-select" value={form.noOfChild} onChange={handleFormChange}>
										{NO_OF_CHILD_OPTIONS.map((n) => (
											<option key={n} value={n}>{n}</option>
										))}
									</select>
								</div>
								<div className="col-md-6">
									<label className="form-label">Reservation/Booking ID <span className="text-danger">*</span></label>
									<input type="text" name="agencyStaff" className="form-control" value={form.agencyStaff} onChange={handleFormChange} placeholder="Reservation/Booking ID" required />
								</div>
								<div className="col-12">
									<label className="form-label">Special Requirement</label>
									<textarea name="specialRequirements" className="form-control" rows={3} value={form.specialRequirements} onChange={handleFormChange} placeholder="Passenger's messages" />
								</div>
							</div>
						</div>
					</div>

					<div className="card mt-4">
						<div className="card-body">
							<h4 className="mb-2">Request Extras</h4>
							<p className="text-muted">
								You have 1 checked and 1 carry-on bag included in your transfer, would you like to request extra luggage or other items for your booking?
							</p>
							{EXTRAS_META.map((meta) => (
								<div className="d-flex justify-content-between align-items-center py-3 border-bottom" key={meta.key}>
									<div className="d-flex align-items-center" style={{ gap: "1rem" }}>
										<img src={meta.image} alt={meta.label} style={{ width: "48px", height: "48px", objectFit: "contain" }} />
										<div>
											<span className="fw-500 d-block">{meta.label}</span>
											<span className="text-muted fs-13">{meta.description}</span>
										</div>
									</div>
									<QuantityStepper
										value={extrasQty[meta.key]}
										min={0}
										max={extrasPricing ? Number(extrasPricing[meta.maxKey]) : 0}
										onChange={(value) => setExtrasQty((prev) => ({ ...prev, [meta.key]: value }))}
									/>
								</div>
							))}
						</div>
					</div>
				</div>

				<div className="col-lg-4">
					<div className="card">
						<div className="card-body">
							<div className="border rounded d-flex align-items-center justify-content-center mb-3" style={{ height: "150px" }}>
								<img src={transferData.vehicle_img} alt="" style={{ maxHeight: "120px", objectFit: "contain" }} />
							</div>
							<h5 className="text-center mb-4">
								{transferData.vehicle_type_name} ({transferData.make})
							</h5>

							<h6 className="text-primary">Vehicle Details</h6>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Vehicle Type</span>
								<span>{transferData.make} {transferData.model}</span>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Max Passengers</span>
								<span>{transferData.max_pax}</span>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Max Luggage</span>
								<span>{transferData.max_lugg}</span>
							</div>
							<div className="d-flex justify-content-between mb-3">
								<span className="fw-500">Description</span>
								<span>{transferData.vehicle_type_des}</span>
							</div>

							<hr />
							<h6 className="text-primary">Search Details</h6>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">From</span>
								<span className="text-end">{searchData.from?.label || "—"}</span>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">To</span>
								<span className="text-end">{searchData.to?.label || "—"}</span>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<span className="fw-500">Date</span>
								<span>{formatSearchDate(searchData.date)}</span>
							</div>
							<div className="d-flex justify-content-between mb-3">
								<span className="fw-500">Time</span>
								<span>{formatSearchTime(searchData.time)}</span>
							</div>

							{EXTRAS_META.some((meta) => extrasQty[meta.key] > 0) && (
								<>
									<hr />
									<h6 className="text-primary">Extra Requests</h6>
									{EXTRAS_META.filter((meta) => extrasQty[meta.key] > 0).map((meta) => (
										<div className="d-flex justify-content-between mb-2" key={meta.key}>
											<span className="fw-500">{meta.label} x{extrasQty[meta.key]}</span>
											<span>AU$ {extrasTotal(meta.key).toFixed(2)}</span>
										</div>
									))}
								</>
							)}

							<hr />
							<h6 className="text-primary">Payment</h6>
							<div className="d-flex justify-content-between mb-3">
								<span className="fw-500">Pay Amount</span>
								<span className="fs-18 fw-bold">AU$ {totalPrice.toFixed(2)}</span>
							</div>
							<button
								type="button"
								className="btn btn-primary rounded-pill w-100"
								disabled={!isFormValid || submitting}
								onClick={handleProceedClick}
							>
								{submitting ? "Processing..." : "Proceed To Payment"} <i className="fa fa-paper-plane ms-2" />
							</button>
							{RECAPTCHA_SITE_KEY && (
								<ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} badge="bottomright" />
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TransferBookingDetails;
